import { Link } from '@tanstack/react-router'
import type { ReactNode } from 'react'
import { Button } from '@/components/atoms'
import { ChevronRightIcon } from '@/components/atoms/icons'

export type PerkCardAccent = 'coral' | 'indigo'

export type PerkCardAction = { label: string } & (
  | { to: string; onClick?: never }
  | { onClick: () => void; to?: never }
)

export interface PerkCardProps {
  image: string
  title: string
  description: ReactNode
  accent?: PerkCardAccent
  action: PerkCardAction
  className?: string
  testId?: string
}

const ACCENTS: Record<PerkCardAccent, string> = {
  coral: '[&_strong]:text-coral-base',
  indigo: '[&_strong]:text-indigo-base',
}

export function PerkCard({
  image,
  title,
  description,
  accent = 'coral',
  action,
  className = '',
  testId,
}: PerkCardProps) {
  const ctaProps = action.to
    ? { as: Link, to: action.to }
    : { onClick: action.onClick }

  return (
    <div
      className={`flex flex-col gap-4 overflow-hidden rounded-2xl border border-indigo-100 bg-white ${className}`}
      data-testid={testId}
    >
      <img
        src={image}
        alt=""
        aria-hidden
        className="h-27 w-full bg-indigo-50 object-cover"
      />
      <div className="flex flex-1 flex-col gap-2 px-4 pb-6">
        <p className="text-base leading-5 font-bold text-neutral-900">
          {title}
        </p>
        <p
          className={`flex-1 text-xs leading-4 text-neutral-900 [&_strong]:font-normal ${ACCENTS[accent]}`}
        >
          {description}
        </p>
        <Button
          variant="secondary"
          size="sm"
          trailingIcon={ChevronRightIcon}
          className="tracking-base h-8 w-full gap-1! py-0 text-xs leading-4"
          data-testid={testId && `${testId}-cta`}
          {...(ctaProps as object)}
        >
          {action.label}
        </Button>
      </div>
    </div>
  )
}
